import { useEffect, useState } from "react";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { Loader2, ShieldCheck, Trash2, UserPlus } from "lucide-react";
import { toast } from "sonner";

import {
  createUserFn,
  deleteUserFn,
  listUsersFn,
  setUserRoleFn,
  type AdminUserRow,
} from "@/server/admin-users.functions";
import type { UserRole } from "@/auth/lucia";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/**
 * Пользователи и роли.
 *
 * Аня на демо: «Ещё чего-то тут нету, нету ролевки, да, то есть кто это зашёл?
 * У нас просто какой-то юзер».
 *
 * Роли и RLS в базе стояли с самого начала, а завести человека или сменить ему
 * роль было нельзя ниоткуда, кроме psql. Серверные функции достались от ядра —
 * здесь свой экран под этот фронт и смена роли, которой в ядре не было.
 *
 * Пароль задаёт админ прямо здесь и передаёт человеку сам. Почты система не
 * отправляет, и притворяться, что «ссылка ушла на email», было бы враньём на
 * экране.
 */
export const Route = createFileRoute("/users")({
  beforeLoad: ({ context }) => {
    // Не «спрятать пункт меню», а закрыть адрес: роль проверяется и на сервере
    // в requireAdmin, но менеджеру незачем видеть даже пустой экран.
    if (context.user?.role !== "admin") throw redirect({ to: "/" });
  },
  component: UsersPage,
});

const ROLE_LABEL: Record<UserRole, string> = {
  admin: "Администратор",
  rop: "Руководитель",
  manager: "Менеджер",
};

const ROLE_NOTE: Record<UserRole, string> = {
  admin: "Видит всё, правит справочники и заводит людей",
  rop: "Видит все калькуляторы, проверяет и правит справочники",
  manager: "Видит свои калькуляторы, справочники — на просмотр",
};

function UsersPage() {
  const [users, setUsers] = useState<AdminUserRow[] | null>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<UserRole>("manager");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void refresh();
  }, []);

  async function refresh() {
    try {
      setUsers(await listUsersFn());
    } catch (e) {
      console.error("[users] не удалось прочитать список", e);
      setError("Не удалось прочитать список пользователей");
    }
  }

  async function create() {
    const mail = email.trim();
    if (!mail || password.length < 6) return;

    setBusy(true);
    setError(null);
    try {
      const res = await createUserFn({ data: { email: mail, password, role } });
      if (!res.ok) {
        setError(res.error);
        return;
      }
      toast.success(`${mail} заведён`, { description: "Пароль передайте человеку сами" });
      setEmail("");
      setPassword("");
      setRole("manager");
      await refresh();
    } catch (e) {
      console.error("[users] не удалось завести пользователя", e);
      setError("Не удалось завести пользователя");
    } finally {
      setBusy(false);
    }
  }

  async function changeRole(user: AdminUserRow, next: UserRole) {
    setError(null);
    // Показываем новую роль сразу: смена мгновенная, а ждать круга до сервера
    // на выпадающем списке заметно. Откатываем, если сервер не согласился.
    setUsers((list) => list?.map((u) => (u.id === user.id ? { ...u, role: next } : u)) ?? null);
    try {
      const res = await setUserRoleFn({ data: { id: user.id, role: next } });
      if (!res.ok) {
        setError(res.error);
        await refresh();
        return;
      }
      toast.success(`${user.email} — ${ROLE_LABEL[next].toLowerCase()}`);
    } catch (e) {
      console.error("[users] не удалось сменить роль", e);
      setError("Не удалось сменить роль");
      await refresh();
    }
  }

  async function remove(user: AdminUserRow) {
    setError(null);
    try {
      const res = await deleteUserFn({ data: { id: user.id } });
      if (!res.ok) {
        setError(res.error);
        return;
      }
      toast.success(`${user.email} удалён`);
      await refresh();
    } catch (e) {
      console.error("[users] не удалось удалить пользователя", e);
      setError("Не удалось удалить пользователя");
    }
  }

  return (
    <div className="max-w-3xl space-y-5">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Пользователи</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Кто заходит в систему и что ему видно. Правит администратор.
        </p>
      </div>

      {error && (
        <p className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive-ink">
          {error}
        </p>
      )}

      <Card>
        <CardContent className="space-y-4 py-4">
          <div className="flex items-center gap-2">
            <UserPlus className="size-4 text-muted-foreground" />
            <span className="text-sm font-medium">Завести человека</span>
          </div>

          <div className="grid gap-3 sm:grid-cols-[1fr_1fr_190px]">
            <div className="space-y-1.5">
              <Label className="text-xs font-normal text-muted-foreground">Почта</Label>
              <Input
                type="email"
                value={email}
                placeholder="vika@braining.ru"
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-normal text-muted-foreground">Пароль</Label>
              {/* Пароль виден намеренно: его тут же диктуют человеку, а точки
                  на экране заставили бы набирать вслепую и ошибаться. */}
              <Input
                type="text"
                value={password}
                placeholder="минимум 6 символов"
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-normal text-muted-foreground">Роль</Label>
              <Select value={role} onValueChange={(v) => setRole(v as UserRole)}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(ROLE_LABEL) as UserRole[]).map((r) => (
                    <SelectItem key={r} value={r}>
                      {ROLE_LABEL[r]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button
              onClick={() => void create()}
              disabled={busy || password.length < 6 || !email.trim()}
            >
              {busy && <Loader2 className="size-4 animate-spin" />}
              Завести
            </Button>
            <span className="text-xs text-muted-foreground">{ROLE_NOTE[role]}</span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-3 py-4">
          <div className="flex items-center gap-2">
            <ShieldCheck className="size-4 text-muted-foreground" />
            <span className="text-sm font-medium">Кто есть</span>
            {users && <span className="text-xs text-muted-foreground">{users.length}</span>}
          </div>

          {users === null ? (
            <p className="text-sm text-muted-foreground">Загружаем…</p>
          ) : (
            <div className="divide-y rounded-md border">
              {users.map((u) => (
                <div key={u.id} className="flex flex-wrap items-center gap-3 px-3 py-2.5">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{u.email}</div>
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      с {new Date(u.createdAt).toLocaleDateString("ru-RU")}
                      {u.sessions > 0 && ` · сейчас в системе`}
                    </div>
                  </div>

                  <Select value={u.role} onValueChange={(v) => void changeRole(u, v as UserRole)}>
                    <SelectTrigger className="h-9 w-[190px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(Object.keys(ROLE_LABEL) as UserRole[]).map((r) => (
                        <SelectItem key={r} value={r}>
                          {ROLE_LABEL[r]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Удаление спрашивает: человек с калькуляторами исчезает
                      вместе с доступом к ним, и отменить это нечем. */}
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive-ink"
                        aria-label={`Удалить ${u.email}`}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Удалить {u.email}?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Человек потеряет доступ, его сессии оборвутся. Собранные им калькуляторы
                          останутся в системе, но открыть их сможет только администратор.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Отмена</AlertDialogCancel>
                        <AlertDialogAction onClick={() => void remove(u)}>
                          Удалить
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              ))}
            </div>
          )}

          <div className="flex flex-wrap gap-x-4 gap-y-1 pt-1 text-xs text-muted-foreground">
            {(Object.keys(ROLE_LABEL) as UserRole[]).map((r) => (
              <span key={r}>
                <Badge variant="outline" className="mr-1.5 font-normal">
                  {ROLE_LABEL[r]}
                </Badge>
                {ROLE_NOTE[r].toLowerCase()}
              </span>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
